import { mapFields } from 'vuex-map-fields'
import videoModalMixin from './videoModalMixin'

export default {
    mixins: [videoModalMixin],

    methods : {
        async getCurrentUser(forceNew = false){

            // if we're currently loading the user wait for the return
            if ( this.cu_loading === true ) {
                return new Promise((resolve, reject) => {
                    this.$on('currentUserSet', (results) => {
                        if (results.success) {
                            resolve(this.currentUser)
                        } else {
                            reject(results.errors)
                        }
                    })
                })
            }

            // if we already have the current user id and it's less than 5 minutes old return
            
            if ( this.currentUser?.id && (Date.now() - this.modified) < 300000 && !forceNew ) {
                this.$emit('currentUserSet', {success: true})
                // Show onboarding task university lesson video if needed
                await this.handleBeforeSettingUser()
                
                return this.currentUser
            }

            this.cu_loading = true
            this.modified = Date.now()

            let {data: {user, errors}} = await this.$http.get('/api/v1/admins/me').catch(e => e)
            if (!user) {
                this.cu_loading = false
                this.$emit('currentUserSet', {success: false, errors})
            }
            
            await this.handleBeforeSettingUser(user)

            if (user) { user.full_name =  (user?.firstname || '') + ' ' + (user?.lastname || '') }
            this.$store.commit('current_user/setCurrentUser', user)
            this.cu_loading = false

            this.$emit('currentUserSet', {success: true})
            return this.currentUser
        },

        async resetCurrentUser(){
            await this.getCurrentUser(true)
        },

        clearCurrentUser(){
            this.currentUser = {}
            this.cu_loading = false
            this.modified = null
        },
        async getCurrentUserBillingProfiles(){
            let { data : { profiles } } = await this.$http.get('/api/v1/billings/getActiveProfiles')
            return profiles
        },

        async handleBeforeSettingUser(user = null) {
            // Payment failure redirect check
            const userToCheck = user !== null && user !== undefined ? user : this.currentUser
            const allowed_paths = ['/ppc/billing', '/agents/billingProfile', '/agents/addBillingProfile', '/agents/support/index'];
            
            const currentPath = window.location.pathname.replace(/\/$/, '');
            
            if (userToCheck?.is_payment_failed && 
                !(allowed_paths.includes(currentPath))) {
                    this.$router.push({name: 'ppc-billing'});
            }

            // Show onboarding video if needed
            await this.handleUserVideoDisplay();

            // Initialize progress footer if not on /agents path
            if (currentPath !== '/agents') {
                //this.injectProgressFooter();
            }
        },
        currentUserCanUse(featureFlag){
            if ( !this.ppc ) return true    // we aren't limiting features on NxLead
            if ( !this.currentUser?.enabled_features ) return false    // prevent things from flashing on screen during hydration
            return this.currentUser.enabled_features.indexOf(featureFlag) !== -1
        },
        currentUserCanUseAny(featureFlags){
            for ( let i in featureFlags ){
                if ( this.currentUserCanUse(featureFlags[i]) ) return true
            }
            return false
        },
        /**
         * Check whether the current user has already completed/skipped a guide (tour).
         * The `guides` field on the admin record is a CSV string of guide keys the
         * user has seen (e.g. "prof,dash"). We check it here so a tour component can
         * decide whether to show without making an extra API call.
         *
         * @param {string} guideKey short guide key, e.g. "prof"
         * @returns {boolean}
         */
        currentUserHasSeenGuide(guideKey){
            const guides = this.currentUser?.guides
            if ( !guides || !guideKey ) return false
            return guides.split(',').includes(guideKey)
        },
        /**
         * Mark a guide (tour) as completed or skipped for the current user. This
         * appends the guide key to the CSV `guides` field, updates local state
         * immediately (so the tour won't re-show this session) and persists the
         * change to the backend via the Admins update-field endpoint.
         *
         * @param {string} guideKey short guide key, e.g. "prof"
         * @returns {Promise<void>}
         */
        async currentUserMarkGuideComplete(guideKey){
            if ( !guideKey || this.currentUserHasSeenGuide(guideKey) ) return

            const existing = this.currentUser?.guides
            const updatedGuides = existing ? `${existing},${guideKey}` : guideKey

            // update local state immediately so we don't re-show the guide this session
            this.$store.commit('current_user/setCurrentUser', {
                ...this.currentUser,
                guides: updatedGuides
            })

            // persist to the backend
            try {
                await this.$http.post('/api/v1/admins/update-profile-fields', {
                    id: this.currentUser.id,
                    fields: { guides: updatedGuides }
                })
            } catch ( e ) {
                console.warn('Failed to persist completed guide:', guideKey, e)
            }
        }
    },

    computed : {
        ...mapFields('current_user', ['currentUser', 'modified', 'currentUserError', 'cu_loading', 'profileImageVersion']),
        client() {
            return this.currentUser?.client
        },
        platform() {
            return this.currentUser?.client?.platform
        },
        nxlead() {
            return this.currentUser?.client?.platform === 'nxlead'
        },
        ppc() {
            return this.currentUser?.client?.platform === "ppc"
        },          
        HouseJetMembershipActive() {
            return this.currentUser?.house_jet_membership?.is_active
        },
        manageUser(){
            return this.currentUser?.SuperadminLogged || this.currentUser?.SuperDeveloperLogged
        },
        isFreeUser(){
            console.warn('getCurrentUserX.isFreeUser is deprecated. Use getCurrentUserX.isFreeSignup')
            return this.currentUser?.zipcode?.free === 1
        },
        HouseJetMembershipActivated(){
            return this.currentUser?.house_jet_membership?.activated
        },
        isFreeSignup(){
            return !!this.currentUser?.house_jet_signups?.free
        },
        isSalesClient(){
            return this.currentUser?.client?.id === '123'
        },
        apiBaseUrl(){
            return window.IS_MOBILE_APP ? 'https://api.housejet.com' : ''
        },
        welcomeVideo(){
            return this.currentUser?.viewed_welcome === 'n' && this.currentUser?.welcome_video
        }
    }
}
